///
/// EarthBrowser
///
/// PressureTrackNode - the path of one tracked pressure centre: a dim solid
/// line for where it has been (frames before the displayed one) and a
/// brighter line with flowing arrows for where the forecast takes it. Same
/// visual grammar as the hurricane tracks so the two read as one family.
/// Positions come from the cross-frame track correlation in
/// SynopticDatasetLayer (track_id); the node itself is rebuilt whenever the
/// displayed frame changes.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { slerpUnit, surfacePoint, unitNormal } from "../hurricanes/stormTrack"

export interface PressureTrackPoint {
	lon: number
	lat: number
	pressureHpa: number
	timeMs: number
}

// Just under the H/L markers (5 km), above the isoline sphere and fronts
const cAltitudeKm = 4.5
const cPastWidth = 1.5
const cFutureWidth = 2.2
const cPastAlpha = 0.45
const cFutureAlpha = 0.85
// Geodesic densification step (radians); ~35 km segments
const cMaxStepRad = 0.0055
// One flowing arrow per this many forecast points (3-hourly frames: 6 h)
const cPointsPerArrow = 2

const cLowColor: color = rgba(1.0, 0.45, 0.4, 1)
const cHighColor: color = rgba(0.45, 0.65, 1.0, 1)

export class PressureTrackNode extends NodeBase {
	/**
	 * @param past Points up to and including the displayed frame, oldest first
	 * @param future Points after the displayed frame, soonest first
	 */
	constructor(context: StovokorContext, centerType: string, past: PressureTrackPoint[], future: PressureTrackPoint[]) {
		super()
		const base = centerType === 'L' ? cLowColor : cHighColor

		if (past.length >= 2) {
			this.addNode(new PolylineNode(context, {
				positions: densify(past),
				width: cPastWidth,
				color: rgba(base.r, base.g, base.b, cPastAlpha),
				depthWrite: false,
			}))
		}

		// Join the forecast to the displayed position so the path is continuous
		const anchor = past[past.length - 1]
		const futurePoints = anchor ? [anchor, ...future] : future
		if (futurePoints.length >= 2) {
			this.addNode(new PolylineNode(context, {
				positions: densify(futurePoints),
				width: cFutureWidth,
				color: rgba(base.r, base.g, base.b, cFutureAlpha),
				arrowCount: Math.max(1, Math.round(future.length / cPointsPerArrow)),
				arrowSpeed: 0.25,
				depthWrite: false,
			}))
		}
	}
}

function densify(points: PressureTrackPoint[]): vec3[] {
	const positions: vec3[] = []
	let prev = unitNormal(points[0].lat, points[0].lon)
	positions.push(surfacePoint(prev, cAltitudeKm))
	for (let i = 1; i < points.length; i++) {
		const next = unitNormal(points[i].lat, points[i].lon)
		const cosAngle = Math.max(-1, Math.min(1, prev.x * next.x + prev.y * next.y + prev.z * next.z))
		const steps = Math.max(1, Math.ceil(Math.acos(cosAngle) / cMaxStepRad))
		for (let s = 1; s <= steps; s++) {
			positions.push(surfacePoint(slerpUnit(prev, next, s / steps), cAltitudeKm))
		}
		prev = next
	}
	return positions
}
